// Configuración de sucursal — 1 pestaña:
//   1) General: PIN, modo prueba, datos cliente, auto-bloqueo.
//
// MOVER_JUEGOS (migration 038): el config de juegos (ruleta/raspadita/memoria)
// dejó de ser por-sucursal y pasó a GLOBAL del shopping. La pestaña "Juegos"
// que vivía acá se mudó a Ajustes del shopping › Juegos (GlobalSettings.tsx).
// MOVER_SONIDO (migration 041): el sonido (estilo + habilitado) también dejó de
// ser por-sucursal y pasó a GLOBAL. La pestaña "Sonidos" se mudó a Ajustes del
// shopping › Sonidos (GlobalSettings.tsx). Queda solo General.
//
// Estructura JSX: un solo <form> con todos los campos. El tab-bar queda con
// una sola pestaña (General); se mantiene por mínimo cambio.

import { useEffect, useState } from "react";
import {
  Save,
  AlertTriangle,
  Loader2,
  Lock,
  KeyRound,
} from "lucide-react";
import type { CampoCliente } from "@juegossc/shared";
import {
  adminGetSettings,
  adminUpdateSettings,
  type AdminSettings,
  type UpdateSettingsBody,
} from "../../lib/api";
import { useAdminCtx } from "./AdminLayout";
import { useDialogs } from "../../hooks/useDialogs";
import { Section } from "../../components/admin/forms/SettingCard";
import { ToggleRow } from "../../components/admin/forms/ToggleRow";
import { NumberField } from "../../components/admin/forms/NumberField";

const CAMPOS_LABELS: Record<CampoCliente, string> = {
  nombre: "Nombre completo",
  telefono: "Teléfono",
  cedula: "Cédula",
  email: "Email",
  ticket: "Nº de ticket",
};

const TODOS_CAMPOS: CampoCliente[] = [
  "nombre",
  "telefono",
  "cedula",
  "email",
  "ticket",
];

// Reorg IA 2026-06: "General" se partio en "Kiosco y seguridad" (PIN,
// auto-bloqueo, modo prueba) y "Datos del cliente" (que se pide antes
// de jugar). Antes estaban todos amontonados bajo "General" sin
// relacion clara entre si.
// Seguridad (PIN/modo prueba/auto-bloqueo) + Datos del cliente van JUNTOS en
// "General": son pocos controles cortos, no justifican 2 pestañas separadas.
// MOVER_JUEGOS (migration 038): la pestaña "Juegos" se mudó a Ajustes del
// shopping (el config de juegos es global ahora).
// MOVER_SONIDO (migration 041): el sonido (estilo + habilitado) dejó de ser
// por-sucursal y pasó a GLOBAL del shopping. La pestaña "Sonidos" que vivía
// acá se mudó a Ajustes del shopping › Sonidos (GlobalSettings.tsx). Queda
// solo General.
type TabKey = "general";
const TABS: { key: TabKey; label: string; Icon: typeof Lock }[] = [
  { key: "general", label: "General", Icon: Lock },
];

export default function Configuracion() {
  const { sucursalIdEfectiva, setPageDirty } = useAdminCtx();
  const dialogs = useDialogs();
  // Al desmontar (cambio de pagina), limpiar el flag de cambios sin
  // guardar para que no quede colgado en el AdminLayout.
  useEffect(() => () => setPageDirty(false), [setPageDirty]);
  const [settings, setSettings] = useState<AdminSettings | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [activeTab, setActiveTab] = useState<TabKey>("general");

  // ── Form state — General
  const [pin, setPin] = useState("");
  const [registrar, setRegistrar] = useState(false);
  const [campos, setCampos] = useState<CampoCliente[]>([]);
  const [obligatorios, setObligatorios] = useState<CampoCliente[]>([]);
  const [modoPrueba, setModoPrueba] = useState(false);
  const [autoBloqueo, setAutoBloqueo] = useState(10);

  // MOVER_JUEGOS (migration 038): todo el form state de Ruleta/Raspadita/Memoria
  // (toggles, físicos, visuales, tiempos, imágenes) + el preview del juego y sus
  // datos auxiliares (campaña, fondo, premios, fuentes) se mudaron a Ajustes del
  // shopping › Juegos (GlobalSettings.tsx). El config de juegos es global ahora.

  // MOVER_SONIDO (migration 041): el form state de Sonidos (soundEnabled,
  // soundStyleSlug, la lista soundStyles y su fetch) se mudó a Ajustes del
  // shopping › Sonidos (GlobalSettings.tsx). El sonido es global ahora.

  const [saving, setSaving] = useState(false);

  // MOVER_JUEGOS (migration 038): los effects que cargaban datos para el preview
  // del juego (imágenes de memoria + icono raspadita + fondo + chrome en-juego,
  // campaña activa, premios reales) y persistMemoriaImagen se mudaron a Ajustes
  // del shopping › Juegos (GlobalSettings.tsx), junto con los controles.

  useEffect(() => {
    setLoading(true);
    adminGetSettings(sucursalIdEfectiva)
      .then((s) => {
        setSettings(s);
        setRegistrar(s.registrar_datos_cliente);
        setCampos(s.campos_cliente);
        setObligatorios(s.campos_obligatorios);
        setModoPrueba(s.modo_prueba);
        setAutoBloqueo(s.auto_bloqueo_minutos);

        // MOVER_JUEGOS (migration 038): el config de juegos ya no se hidrata acá
        // (se editaba por sucursal); ahora vive en Ajustes del shopping › Juegos.
        // MOVER_SONIDO (migration 041): el sonido tampoco se hidrata acá — pasó a
        // global (Ajustes del shopping › Sonidos).
      })
      .catch((e) => setError(e.message))
      .finally(() => setLoading(false));
  }, [sucursalIdEfectiva]);

  function toggleCampo(c: CampoCliente) {
    setCampos((cs) =>
      cs.includes(c) ? cs.filter((x) => x !== c) : [...cs, c],
    );
    if (campos.includes(c)) {
      setObligatorios((os) => os.filter((x) => x !== c));
    }
  }

  function toggleObligatorio(c: CampoCliente) {
    setObligatorios((os) =>
      os.includes(c) ? os.filter((x) => x !== c) : [...os, c],
    );
  }

  async function handleSave(e: React.FormEvent) {
    e.preventDefault();
    setError(null);

    if (modoPrueba && !settings?.modo_prueba) {
      const ok = await dialogs.confirm({
        title: "Activar modo prueba",
        message:
          "Vas a activar el MODO PRUEBA. Las jugadas NO se registrarán mientras esté activo. ¿Confirmás?",
        confirmLabel: "Activar modo prueba",
        variant: "destructive",
      });
      if (!ok) {
        setModoPrueba(false);
        return;
      }
    }

    // MOVER_JUEGOS (migration 038): el config de juegos (ruleta/raspadita/memoria)
    // ya NO se guarda acá — se editaba por sucursal y ahora es global (se guarda
    // desde Ajustes del shopping › Juegos).
    // MOVER_SONIDO (migration 041): el sonido tampoco se guarda acá — pasó a
    // global. El body de sucursal queda con seguridad + datos del cliente.
    const body: UpdateSettingsBody = {
      registrar_datos_cliente: registrar,
      campos_cliente: campos,
      campos_obligatorios: obligatorios,
      modo_prueba: modoPrueba,
      auto_bloqueo_minutos: autoBloqueo,
    };
    if (pin.length > 0) body.pin = pin;

    setSaving(true);
    try {
      await adminUpdateSettings(body, sucursalIdEfectiva);
      setPin("");
      const fresh = await adminGetSettings(sucursalIdEfectiva);
      setSettings(fresh);
      setPageDirty(false);
      dialogs.toast({
        message: "Configuración guardada correctamente.",
        variant: "success",
      });
    } catch (e) {
      setError(e instanceof Error ? e.message : String(e));
    } finally {
      setSaving(false);
    }
  }

  if (loading) {
    return (
      <div className="flex h-48 items-center justify-center">
        <Loader2 className="h-8 w-8 animate-spin opacity-50" />
      </div>
    );
  }

  return (
    // Full-width (reorg UX 2026-06-25): antes `mx-auto max-w-3xl` capaba el
    // contenido en una columna angosta. Ahora usa todo el ancho como Faqs.
    // El form sigue en una sola columna (es un form, no un grid de cards),
    // pero los inputs nativos no se estiran (width por defecto), asi que el
    // ancho extra lo aprovechan los previews y las grids internas (md:grid).
    <div>
      <h1 className="sr-only">Mi pantalla</h1>

      {/* MOVER_JUEGOS (migration 038): ya no hay preview de juego acá (la pestaña
          Juegos se mudó a Ajustes del shopping). Sin riel fijo, se usa todo el
          ancho en las dos pestañas restantes. */}
      <div>
      {settings?.modo_prueba && (
        <div className="mb-6 flex items-center gap-3 rounded-lg bg-yellow-500/15 p-4 text-sm">
          <AlertTriangle className="h-5 w-5 text-yellow-400 shrink-0" />
          <div>
            <strong>Modo prueba activo.</strong> Las jugadas NO se están
            registrando. Desactivá abajo cuando termines de probar.
          </div>
        </div>
      )}

      {/* Aviso bloqueante de PIN: sin PIN la pantalla no se puede activar.
          Reorg UX 2026-06: lo subimos a un banner arriba de las tabs para
          que el admin lo vea antes de invertir tiempo en lo cosmetico, en
          vez de descubrirlo recien al activar la pantalla del cliente. */}
      {settings && !settings.pin_configurado && (
        <div className="mb-6 flex items-center gap-3 rounded-lg bg-red-500/15 p-4 text-sm">
          <KeyRound className="h-5 w-5 shrink-0 text-red-400" />
          <div>
            <strong>Falta configurar el PIN.</strong> La pantalla no se puede
            activar hasta que cargues un PIN en{" "}
            <strong>Seguridad</strong> (la primera pestaña).
          </div>
        </div>
      )}

      {/* Pestañas — General (única tras mover Juegos y Sonidos a Ajustes del
          shopping). Los inputs viven dentro del form y solo cambia visibilidad:
          ocultar con CSS preserva estado al cambiar de pestaña. */}
      <div className="mb-6 flex gap-1 overflow-x-auto border-b border-white/10">
        {TABS.map(({ key, label, Icon }) => (
          <button
            key={key}
            type="button"
            onClick={() => setActiveTab(key)}
            className={`flex items-center gap-2 border-b-2 px-4 py-3 text-sm font-medium transition ${
              activeTab === key
                ? "border-[#a78bfa] text-white"
                : "border-transparent text-white/60 hover:text-white"
            }`}
          >
            <Icon className="h-4 w-4" />
            {label}
          </button>
        ))}
      </div>

      {/* onChange en el form marca "cambios sin guardar" (cubre todos los
          inputs nativos: PIN, toggles, sliders, selects, numeros). Los
          controles de botones (alineacion, estilo de sonido) lo marcan
          aparte en su onClick. */}
      <form
        onSubmit={handleSave}
        onChange={() => setPageDirty(true)}
        className="flex flex-col gap-6"
      >
        {/* ──────────── TAB GENERAL (seguridad + datos del cliente) ──────────── */}
        {/* PIN / Modo prueba / Auto-bloqueo / Datos del cliente: 4 controles
            cortos en grid 2×2. Ninguno a todo el ancho (pedido), sin huecos. */}
        <div className={activeTab === "general" ? "grid grid-cols-1 items-start gap-4 md:grid-cols-2" : "hidden"}>
          <Section title="PIN de la pantalla">
            <p className="text-sm opacity-70">
              {settings?.pin_configurado ? (
                <>El PIN está configurado. Dejá vacío para no cambiarlo.</>
              ) : (
                <>
                  <span className="text-yellow-400">PIN no configurado.</span>{" "}
                  La pantalla no podrá activarse hasta que lo configures.
                </>
              )}
            </p>
            <input
              type="password"
              inputMode="numeric"
              value={pin}
              onChange={(e) => setPin(e.target.value)}
              placeholder={
                settings?.pin_configurado
                  ? "Dejá vacío para no cambiar"
                  : "Ingresá un PIN"
              }
              className="mt-2 rounded-lg bg-white/10 px-3 py-2 outline-none focus:ring-2 focus:ring-[#a78bfa]"
            />
          </Section>

          <div data-tour="config-modo-prueba">
            <Section title="Modo prueba">
              <ToggleRow
                checked={modoPrueba}
                onChange={setModoPrueba}
                label="Activar modo prueba"
                desc="Las jugadas no se registran."
                help="Útil para verificar la configuración sin ensuciar las estadísticas. Se apaga solo a las 24h por si te olvidás de desactivarlo."
              />
            </Section>
          </div>

          <Section title="Auto-bloqueo">
            <label className="flex flex-col gap-1 text-sm">
              <span className="opacity-80">
                Minutos sin interacción antes de bloquear
              </span>
              <NumberField
                value={autoBloqueo}
                onChange={setAutoBloqueo}
                min={1}
                max={60}
                step={1}
                ariaLabel="Minutos sin interacción antes de bloquear"
              />
            </label>
          </Section>

          {/* Datos del cliente — una celda más (no full-width). */}
          <div>
          <Section title="Datos del cliente">
            <ToggleRow
              checked={registrar}
              onChange={setRegistrar}
              label="Pedir datos del cliente antes de jugar"
              desc="Si está OFF, el cliente juega directo."
              help="Si está ON, un formulario le pide los datos al cliente antes de cada jugada."
            />

            {registrar && (
              <div className="mt-4 rounded-lg bg-black/30 p-4">
                <table className="w-full text-sm">
                  <thead className="text-left opacity-70">
                    <tr>
                      <th className="pb-2">Campo</th>
                      <th className="w-20 pb-2 text-center">Pedir</th>
                      <th className="w-24 pb-2 text-center">Obligatorio</th>
                    </tr>
                  </thead>
                  <tbody>
                    {TODOS_CAMPOS.map((c) => (
                      <tr key={c}>
                        <td className="py-1">{CAMPOS_LABELS[c]}</td>
                        <td className="py-1 text-center">
                          <input
                            type="checkbox"
                            checked={campos.includes(c)}
                            onChange={() => toggleCampo(c)}
                            className="h-4 w-4"
                          />
                        </td>
                        <td className="py-1 text-center">
                          <input
                            type="checkbox"
                            checked={obligatorios.includes(c)}
                            onChange={() => toggleObligatorio(c)}
                            disabled={!campos.includes(c)}
                            className="h-4 w-4 disabled:opacity-30"
                          />
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </Section>
          </div>
        </div>

        {/* MOVER_SONIDO (migration 041): la pestaña "Sonidos" (toggle + selector
            de estilos) se mudó a Ajustes del shopping › Sonidos
            (GlobalSettings.tsx). El sonido es global del shopping ahora. */}

        {error && (
          <div className="rounded-lg bg-red-500/15 p-4 text-sm text-red-300">
            {error}
          </div>
        )}

        {/* Barra de guardado sticky (reorg UX 2026-06): el boton queda
            siempre a mano aunque la pagina sea larga. Feedback de exito
            por toast. */}
        <div className="sticky bottom-0 z-10 -mx-1 mt-2 rounded-t-xl bg-[#0f0c29]/85 py-2 backdrop-blur">
          <button
            type="submit"
            disabled={saving}
            className="flex w-full items-center justify-center gap-2 rounded-lg bg-gradient-to-r from-[#a78bfa] to-[#7c3aed] py-2.5 font-bold disabled:opacity-50"
          >
            {saving ? (
              <Loader2 className="h-5 w-5 animate-spin" />
            ) : (
              <Save className="h-5 w-5" />
            )}
            Guardar cambios
          </button>
        </div>
      </form>
      </div>
    </div>
  );
}
